"use client";

import { Core } from "@faire/design-tokens";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { NonBreaking } from "@faire/web/common/string";
import { Button } from "@faire/web/slate/Button";
import { Column, Flex } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import capitalize from "lodash/capitalize";
import React, { Suspense } from "react";
import styled from "styled-components";

import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/retailer/lib/popSignUp";
import {
  DiscoverPageImageryBannerPhase3,
  VerticalizedBannerLoadingSkeleton,
} from "@faire/retailer/views/search-v2/LoggedOutDiscoverHeaderBanner/LoggedOutDiscoverPageHeroBannerPhase3";
import { useIsDiscoverRedesignPhase3XP } from "@faire/retailer/views/search-v2/LoggedOutDiscoverHeaderBanner/useIsDiscoverRedesignPhase3XP";
import { useIsLoggedOutDiscoverPage } from "@faire/retailer/views/search-v2/SearchPageQueryParams/useIsLoggedOutDiscoverPage";
import { useSearchPageProductSearchString } from "@faire/retailer/views/search-v2/SearchPageQueryParams/useSearchPageProductSearchString";

const DiscoverPageHeroText: React.FC = () => {
  const productSearchString = capitalize(useSearchPageProductSearchString());

  return (
    <ContentWrapper>
      <Typography
        variant={{
          mobileAndAbove: "pageHeaderSerifRegular",
          tabletAndAbove: "displaySSerifRegular",
          desktopAndAbove: "displayMSerifRegular",
          xLargeAndAbove: "displayMSerifRegular",
        }}
        as="h1"
        color={Core.text.inverse}
      >
        <StrictLocalMsg
          defaultMessage="Wholesale {queryProductName} for your{nbsp}store"
          description={{
            text: "header for discover page explaining you can buy the item at wholesale prices",
            img: "https://cdn.faire.com/fastly/7ab0bf0cdb8db14e66933ffe35b46db73faa9f353d04a80d140b134967137191.png",
          }}
          values={{
            queryProductName: productSearchString,
            nbsp: NonBreaking.space,
          }}
        />
      </Typography>
      <Typography
        variant={{
          mobileAndAbove: "paragraphSansRegular",
          desktopAndAbove: "subheadingSansRegular",
        }}
        color={Core.text.inverse}
      >
        <StrictLocalMsg
          defaultMessage="From frames to decorative objects and everything in between, whatever you're looking for, find it on{nbsp}Faire."
          description={{
            text: "subheading for discover page, encouraging retailers to browse the site",
            img: "https://cdn.faire.com/fastly/7ab0bf0cdb8db14e66933ffe35b46db73faa9f353d04a80d140b134967137191.png",
          }}
          values={{
            nbsp: NonBreaking.space,
          }}
        />
      </Typography>
    </ContentWrapper>
  );
};

const DiscoverPageImageryBanner: React.FC = () => {
  const onSignUpClick = () => {
    popSignUp(SignUpType.GENERIC, () => {}, {
      element: SignUpElement.TOP_BANNER,
      sourceId: "logged_out_discover_hero_banner",
    });
  };

  const isSSR = useIsSSR();
  const imgSrc = useResponsiveValue({
    mobileAndAbove: optimizeImageUrl(
      {
        url: "https://cdn.faire.com/fastly/4f09f933fa8f71f181a9df739f86f7e97369b3f92b457122be5419b40e20ddff.jpeg",
      },
      isSSR
    ),
    tabletAndAbove: optimizeImageUrl(
      {
        url: "https://cdn.faire.com/fastly/ac4f8ef285f5e2ce9d493addc2c756c897339d7dff234a56e311ec4d659aa24e.jpeg",
      },
      isSSR
    ),
    desktopAndAbove: optimizeImageUrl(
      {
        url: "https://cdn.faire.com/fastly/47f5406bc2522e2b276072f64a9b376bb7013332c5cbfb185a5771a3023b691e.png",
      },
      isSSR
    ),
  });

  return (
    <Flex
      direction={{ mobileAndAbove: "column-reverse", tabletAndAbove: "row" }}
    >
      <BannerTextWrapper>
        <DiscoverPageHeroText />

        <Button
          onClick={onSignUpClick}
          variant="secondary"
          data-test-id="banner-cta-button"
        >
          <StrictLocalMsg
            defaultMessage="Sign up for free"
            description={{
              text: "sign up cta for discover page header",
              img: "https://cdn.faire.com/fastly/d21596fe32412c4ed5e66b8126825c2cec443186715ab55c7eda5f5488718062.png",
            }}
          />
        </Button>
      </BannerTextWrapper>
      <BannerImageWrapper>
        <BannerImage src={imgSrc} data-test-id="banner-image" />
      </BannerImageWrapper>
    </Flex>
  );
};

export const LoggedOutDiscoverPageHeroBanner: React.FC = () => {
  const isLoggedOutDiscover = useIsLoggedOutDiscoverPage();
  const isDiscoverRedesignPhase3XP = useIsDiscoverRedesignPhase3XP();

  if (!isLoggedOutDiscover) {
    return null;
  }

  if (isDiscoverRedesignPhase3XP) {
    return (
      <Suspense fallback={<VerticalizedBannerLoadingSkeleton />}>
        <DiscoverPageImageryBannerPhase3 />
      </Suspense>
    );
  }

  return <DiscoverPageImageryBanner />;
};

const ContentWrapper = styled(Column).attrs({
  align: "flex-start",
  gap: { mobileAndAbove: "1x", tabletAndAbove: "2x" },
})`
  word-break: break-word;
  ${responsiveMediaQueries.xLargeAndAbove} {
    width: 550px;
    padding-right: ${getSpacing("8x")};
  }
  ${responsiveMediaQueries.desktop} {
    width: 480px;
  }
  ${responsiveMediaQueries.tablet} {
    width: 370px;
  }
`;

const BannerTextWrapper = styled(Flex).attrs({
  direction: "column",
  justify: "space-between",
  align: "flex-start",
})`
  background-color: #083114; // TODO: ADD TOKEN
  width: auto;
  gap: ${getSpacing("3x")};
  padding: ${getSpacing("3x")};
  ${responsiveMediaQueries.desktopAndAbove} {
    padding-top: ${getSpacing("9x")};
    padding-left: max(calc((100vw - 1808px) / 2), 56px);
    padding-bottom: ${getSpacing("9x")};
  }
  ${responsiveMediaQueries.tablet} {
    padding: ${getSpacing("6x", "3x", "6x", "3x")};
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    padding-right: ${getSpacing("2x")};
  }
`;

const BannerImageWrapper = styled.div`
  flex-basis: 80%;
  ${responsiveMediaQueries.mobile} {
    max-height: 190px;
  }
`;

const BannerImage = styled.img`
  width: 100%;
  object-fit: cover;
  height: 0;
  min-height: 100%;
  object-position: center;
  ${responsiveMediaQueries.mobile} {
    height: 100%;
    max-height: 190px;
  }
`;
